import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Image } from 'rebass';
import {
  Typography,
  Menu,
  IconButton,
  Avatar,
  ConfirmationDialog,
} from '@oca/ui';
import { MenuDots, Timer, Pencil, Check, Delete, ClockDash } from '@oca/icons';
import { Trans, t } from '@lingui/macro';
import { SkeletonRenderer } from '@lib/skeleton-loader';
import { i18n } from '@lib/i18n';
import logoTransparent from '../../common/assets/logo.svg';

export function ViewHeader({
  title,
  project,
  isLoading,
  onComplete,
  onArchive,
  onEdit,
}) {
  // eslint-disable-next-line no-nested-ternary
  const status = project
    ? project.completed
      ? i18n._(t`Completed`)
      : i18n._(t`Open`)
    : i18n._(t`N/A`);

  return (
    <Flex bg="white" alignItems="center">
      <Box>
        <SkeletonRenderer
          width={80}
          height={80}
          boxProps={{ mr: 3 }}
          loading={isLoading}
          render={() => (
            <Avatar
              src={project ? project.avatar : null}
              size={64}
              borderRadius="12px"
              mr={3}
            >
              <Image
                src={logoTransparent}
                alt="logo"
                width={100}
                height={100}
                objectFit="contain"
              />
            </Avatar>
          )}
        />
        <Box mt={24}>
          <SkeletonRenderer
            width={180}
            height={24}
            loading={isLoading}
            boxProps={{ mb: 2 }}
            render={() => (
              <Typography
                variant="h1"
                fontWeight={500}
                fontSize={32}
                gutterBottom
              >
                {title}
              </Typography>
            )}
          />
          <Flex alignItems="center" style={{ gap: 8 }} mt={16}>
            <Timer />
            <Typography fontWeight={500} color="textSecondary">
              <Trans>Status</Trans>
            </Typography>
            <Flex alignItems="center" style={{ gap: 8, marginLeft: 120 }}>
              <ClockDash color="#F79009" />
              <Typography color="textSecondary">{status}</Typography>
            </Flex>
          </Flex>
        </Box>
      </Box>
      {project && project.isOwner && (
        <Menu
          usePortal
          anchor={
            <IconButton color="text" padding={1}>
              <MenuDots />
            </IconButton>
          }
        >
          <Menu.Item icon={<Pencil />} onClick={onEdit}>
            <Typography>
              <Trans>Edit</Trans>
            </Typography>
          </Menu.Item>
          {canCompleteProject(project) && (
            <ConfirmationDialog
              title={i18n._(t`Complete ${project.name}?`)}
              description={
                <Trans>
                  This action will complete this project. Are you sure you want
                  to continue?
                </Trans>
              }
              confirmText={<Trans>Complete</Trans>}
            >
              {confirm => (
                <Menu.Item
                  icon={<Check />}
                  onClick={confirm(() => onComplete(project))}
                >
                  <Typography>
                    <Trans>Complete</Trans>
                  </Typography>
                </Menu.Item>
              )}
            </ConfirmationDialog>
          )}
          {project && project.completed && (
            <ConfirmationDialog
              title={i18n._(t`Archive ${project.name}?`)}
              description={
                <Trans>
                  This action will archive this project. Are you sure you want
                  to continue?
                </Trans>
              }
            >
              {confirm => (
                <Menu.Item
                  icon={<Delete />}
                  onClick={confirm(() => onArchive(project))}
                >
                  <Typography>
                    <Trans>Archive</Trans>
                  </Typography>
                </Menu.Item>
              )}
            </ConfirmationDialog>
          )}
        </Menu>
      )}
    </Flex>
  );
}

ViewHeader.propTypes = {
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  project: PropTypes.shape({
    avatar: PropTypes.string,
  }),
  isLoading: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

function canCompleteProject(project) {
  return (
    project &&
    project.completed !== true &&
    project.tasksCount === project.tasksStatuses.done
  );
}
